import {
  Box,
  ModalBody,
  ModalFoot,
  ModalHead,
  Text,
} from '@dixa/lib-shared-habla';
import { useCallback, useState } from 'react';

import { FlowNodeTypes } from 'components/powerFlowShared/components/types';
import { getConfirmButtonVerb } from 'components/powerFlowShared/helpers/getConfirmButtonVerb';
import { useIsChanged } from 'components/powerFlowShared/hooks/useIsChanged';
import { AdapterTypes } from 'components/shared/announcementPicker/adapters';
import { AnnouncementItem } from 'components/shared/announcementPicker/adapters/announcementAdapter';
import { AnnouncementPicker } from 'components/shared/announcementPicker/announcementPicker';
import type { FlowManagerEditModalComponentT } from 'fable/flowEditor';

import type { FlowCallNodeRecordConversation } from '../nodeTypes';

import styles from './recordConversation.module.scss';

type Props = FlowManagerEditModalComponentT<FlowCallNodeRecordConversation>;

export function RecordConversation({
  node,
  nodeLabel,
  triggerClose,
  replaceNode,
}: Props): JSX.Element {
  const { payload } = node;
  const { announcement: payloadAnnouncement, nextNode: savedNextNode } =
    payload || {};
  const savedAnnouncement = payloadAnnouncement?.file ?? null;

  const [announcement, setAnnouncement] = useState<AnnouncementItem | null>(
    savedAnnouncement,
  );

  const isActionNode = node.nodeType === 'action';
  const isChanged = useIsChanged([savedAnnouncement, announcement]);

  const handleSave = useCallback(() => {
    const payload: FlowCallNodeRecordConversation = {
      ...node,
      icon: 'i-call-record',
      isAction: false,
      isEditable: true,
      isRemovable: true,
      nodeType: FlowNodeTypes.RECORD_CONVERSATION,
      overlay: [],
      payload: {
        announcement: announcement
          ? {
              file: announcement,
            }
          : null,
        nextNode: savedNextNode ?? null,
      },
      text: 'Call Record',
    };

    replaceNode(payload);
    triggerClose();
  }, [node, announcement, savedNextNode, replaceNode, triggerClose]);

  return (
    <Box width="600px">
      <ModalHead title={nodeLabel} onClose={triggerClose} />
      <ModalBody>
        <Text.Paragraph subdued>
          All conversations after this action in the flow will be recorded. The
          recording will be stored under Conversations.
        </Text.Paragraph>
        <Text.Paragraph className={styles.notice} subdued>
          <strong>Important:</strong> You are responsible for using Dixa’s
          recording feature in compliance with all applicable laws. Please note
          that certain jurisdictions may require consent from the end-user
          (customer) or agent prior to initiating a recording, and by enabling
          call recording, you confirm that you’ve received such consent.
        </Text.Paragraph>
        <AnnouncementPicker
          adapter={AdapterTypes.Announcement}
          clearable
          onClear={() => setAnnouncement(null)}
          onSelect={setAnnouncement}
          selectedAudio={announcement}
        />
      </ModalBody>
      <ModalFoot
        confirmOnClick={handleSave}
        confirmName={getConfirmButtonVerb(node)}
        confirmProps={{ disabled: !isActionNode && !isChanged }}
        cancelOnClick={triggerClose}
      />
    </Box>
  );
}
